/* ========== HISTOGRAM & PARETO CHARTS ==========
 * Subtypes:
 *  - Histogram (drawHistogramChart, withPareto=false)
 *  - Pareto (drawHistogramChart, withPareto=true)
 *
 * The chartEx data for a histogram is the RAW source values (one number
 * per source cell) — Excel bins them at render time. Binning rules come
 * from <cx:binning> (chartObj.binning: binSize or binCount); when absent
 * we use Scott's normal reference rule, which is Excel's "Automatic".
 *
 * Pareto charts with explicit categories (one bar per category) are NOT
 * binned: the bars are the category values sorted descending, with the
 * cumulative-% line overlaid.
 * =============================================== */

import { COLORS, drawAxes, drawGridlines } from './utils';

/** Bin raw values into { labels, counts } following Excel's rules. */
function binValues(rawValues, binning) {
  var vals = rawValues.filter(function (v) { return typeof v === 'number' && isFinite(v); });
  if (vals.length === 0) return { labels: [], counts: [] };
  var min = Math.min.apply(null, vals);
  var max = Math.max.apply(null, vals);

  var binSize;
  if (binning && binning.binSize > 0) {
    binSize = binning.binSize;
  } else if (binning && binning.binCount > 0) {
    binSize = (max - min) / binning.binCount || 1;
  } else {
    // Scott's normal reference rule (Excel "Automatic")
    var mean = vals.reduce(function (a, b) { return a + b; }, 0) / vals.length;
    var sd = Math.sqrt(vals.reduce(function (a, v) { return a + (v - mean) * (v - mean); }, 0) / vals.length) || 1;
    binSize = (3.5 * sd) / Math.cbrt(vals.length) || 1;
  }
  if (!(binSize > 0)) binSize = 1;

  var numBins = Math.max(1, Math.ceil((max - min) / binSize) || 1);
  if (numBins > 60) { numBins = 60; binSize = (max - min) / numBins || 1; }

  var counts = new Array(numBins).fill(0);
  vals.forEach(function (v) {
    var idx = Math.min(numBins - 1, Math.floor((v - min) / binSize));
    counts[idx]++;
  });

  var fmt = function (n) {
    return Math.abs(n) >= 100 || n % 1 === 0 ? Math.round(n).toString() : n.toFixed(1);
  };
  var labels = counts.map(function (_, i) {
    var lo = min + i * binSize;
    var hi = i === numBins - 1 ? max : min + (i + 1) * binSize;
    return '[' + fmt(lo) + ', ' + fmt(hi) + (i === numBins - 1 ? ']' : ')');
  });
  return { labels: labels, counts: counts };
}

export function drawHistogramChart(ctx, x, y, width, height, chartObj, withPareto) {
  var seriesArr = chartObj && chartObj.series ? chartObj.series : [];
  if (seriesArr.length === 0) return;
  var series = seriesArr[0];
  var raw = series.data;
  if (!raw || raw.length === 0) return;
  var srcCats = chartObj.categories || [];

  var data, cats;
  if (withPareto && srcCats.length >= raw.length && srcCats.some(function (c) { return c !== ''; })) {
    // Pareto over explicit categories: one bar per category, sorted
    // descending by value (bars + categories move together).
    var pairs = raw.map(function (v, i) { return { v: v, c: srcCats[i] || ('' + (i + 1)) }; })
                   .sort(function (a, b) { return b.v - a.v; });
    data = pairs.map(function (p) { return p.v; });
    cats = pairs.map(function (p) { return p.c; });
  } else {
    // Histogram (and category-less Pareto): bin the raw values.
    var binned = binValues(raw, chartObj.binning);
    data = binned.counts;
    cats = binned.labels;
    if (withPareto) {
      var zipped = data.map(function (v, i) { return { v: v, c: cats[i] }; })
                       .sort(function (a, b) { return b.v - a.v; });
      data = zipped.map(function (p) { return p.v; });
      cats = zipped.map(function (p) { return p.c; });
    }
  }
  if (data.length === 0) return;

  var maxVal = Math.max.apply(null, data.concat([0])) || 100;
  var minVal = 0;
  var range = maxVal - minVal || 100;
  var numBins = data.length;

  drawGridlines(ctx, x, y, width, height, minVal, range);

  var binWidth = width / numBins;
  var barColor = series.color || COLORS[0];
  data.forEach(function (val, i) {
    var barH = ((val - minVal) / range) * height;
    var barX = x + i * binWidth;
    var barY = y + height - barH;
    ctx.fillStyle = barColor;
    ctx.fillRect(barX + 1, barY, binWidth - 2, barH);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 0.5;
    ctx.strokeRect(barX + 1, barY, binWidth - 2, barH);
  });

  // Pareto — overlay cumulative percentage line over the (already sorted) bars
  if (withPareto) {
    var total = data.reduce(function (s, v) { return s + v; }, 0) || 1;
    var cumulative = 0;
    ctx.strokeStyle = '#ED7D31';
    ctx.lineWidth = 2;
    ctx.beginPath();
    data.forEach(function (val, i) {
      cumulative += val;
      var pct = cumulative / total;
      var px = x + i * binWidth + binWidth / 2;
      var py = y + height - pct * height;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.stroke();
    cumulative = 0;
    data.forEach(function (val, i) {
      cumulative += val;
      var pct = cumulative / total;
      var px = x + i * binWidth + binWidth / 2;
      var py = y + height - pct * height;
      ctx.fillStyle = '#ED7D31';
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  var maxLabels = Math.floor(width / 42);
  var step = Math.max(1, Math.ceil(numBins / maxLabels));
  for (var i = 0; i < numBins; i += step) {
    ctx.fillStyle = '#666';
    ctx.font = '8px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(cats[i] || ('' + (i + 1)), x + i * binWidth + binWidth / 2, y + height + 6);
  }

  drawAxes(ctx, x, y, width, height);
}
